import {IconEye, IconEyeOff} from "@tabler/icons-react";
import {forwardRef, useId, useState} from "react";
import css from "./glass-input.module.css";

const cx = (...classes) => classes.filter(Boolean).join(" ");

/**
 * Unified glass-style input for the entire application.
 * Supports text, email, password, search, number, tel, url and textarea.
 *
 * @param {object} props
 * @param {"text"|"email"|"password"|"search"|"number"|"tel"|"url"} [props.type]
 * @param {string} [props.label] - Floating label text
 * @param {string} [props.placeholder]
 * @param {import("react").ReactNode} [props.icon] - Left icon element
 * @param {import("react").ReactNode} [props.suffix] - Right element (overridden by password toggle when type="password")
 * @param {string} [props.error] - Error message string
 * @param {boolean} [props.disabled]
 * @param {string} [props.className] - Extra class on the wrapper
 */
const GlassInput = forwardRef(
  (
    {
      type = "text",
      label,
      placeholder,
      icon,
      suffix,
      error,
      disabled,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const uid = useId();
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");
    const [showPassword, setShowPassword] = useState(false);

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const hasValue = String(currentValue).length > 0;
    const isFloating = focused || hasValue;
    const isPassword = type === "password";
    const inputType = isPassword ? (showPassword ? "text" : "password") : type;

    const handleChange = e => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleFocus = e => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = e => {
      setFocused(false);
      onBlur?.(e);
    };

    const fieldClasses = cx(
      css.field,
      label && css.fieldWithLabel,
      icon && css.fieldWithIcon,
      (isPassword || suffix) && css.fieldWithSuffix,
      error && css.fieldError
    );

    const labelClasses = cx(css.label, icon && !isFloating && css.labelWithIcon, isFloating && css.labelFloating);

    return (
      <div className={cx(css.wrapper, className)}>
        {icon && <span className={css.iconLeft}>{icon}</span>}

        <input
          ref={ref}
          id={uid}
          type={inputType}
          className={fieldClasses}
          placeholder={label ? (isFloating ? placeholder : "") : placeholder}
          disabled={disabled}
          value={currentValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${uid}-error` : undefined}
          {...rest}
        />

        {label && (
          <label
            htmlFor={uid}
            className={labelClasses}
          >
            {label}
          </label>
        )}

        {isPassword && (
          <button
            type="button"
            className={css.suffix}
            onClick={() => setShowPassword(prev => !prev)}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <IconEyeOff
                size={18}
                stroke={2}
              />
            ) : (
              <IconEye
                size={18}
                stroke={2}
              />
            )}
          </button>
        )}

        {!isPassword && suffix && <span className={css.suffix}>{suffix}</span>}

        {error && (
          <span
            id={`${uid}-error`}
            className={css.errorMessage}
            role="alert"
          >
            {error}
          </span>
        )}
      </div>
    );
  }
);

GlassInput.displayName = "GlassInput";

/**
 * Glass-style textarea.
 *
 * @param {object} props
 * @param {string} [props.label] - Floating label
 * @param {string} [props.error] - Error message
 * @param {number} [props.rows] - Visible rows
 */
const GlassTextarea = forwardRef(
  (
    {
      label,
      placeholder,
      icon,
      error,
      disabled,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      rows = 4,
      ...rest
    },
    ref
  ) => {
    const uid = useId();
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const hasValue = String(currentValue).length > 0;
    const isFloating = focused || hasValue;

    const handleChange = e => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleFocus = e => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = e => {
      setFocused(false);
      onBlur?.(e);
    };

    const fieldClasses = cx(
      css.field,
      css.textarea,
      label && css.fieldWithLabel,
      icon && css.fieldWithIcon,
      error && css.fieldError
    );

    const labelClasses = cx(css.label, icon && !isFloating && css.labelWithIcon, isFloating && css.labelFloating);

    return (
      <div className={cx(css.wrapper, className)}>
        {icon && <span className={css.iconLeft}>{icon}</span>}

        <textarea
          ref={ref}
          id={uid}
          className={fieldClasses}
          placeholder={label ? (isFloating ? placeholder : "") : placeholder}
          disabled={disabled}
          value={currentValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          rows={rows}
          aria-invalid={!!error}
          aria-describedby={error ? `${uid}-error` : undefined}
          {...rest}
        />

        {label && (
          <label
            htmlFor={uid}
            className={labelClasses}
          >
            {label}
          </label>
        )}

        {error && (
          <span
            id={`${uid}-error`}
            className={css.errorMessage}
            role="alert"
          >
            {error}
          </span>
        )}
      </div>
    );
  }
);

GlassTextarea.displayName = "GlassTextarea";

/**
 * Glass-style native select.
 *
 * @param {object} props
 * @param {string} [props.label] - Floating label
 * @param {string} [props.error] - Error message
 * @param {Array<{value: string, label: string}>} [props.options] - Select options
 */
const GlassSelect = forwardRef(
  (
    {
      label,
      placeholder,
      icon,
      error,
      disabled,
      className,
      value,
      defaultValue,
      onChange,
      onFocus,
      onBlur,
      options = [],
      children,
      ...rest
    },
    ref
  ) => {
    const uid = useId();
    const [focused, setFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");

    const isControlled = value !== undefined;
    const currentValue = isControlled ? value : internalValue;
    const hasValue = String(currentValue).length > 0;
    const isFloating = focused || hasValue;

    const handleChange = e => {
      if (!isControlled) setInternalValue(e.target.value);
      onChange?.(e);
    };

    const handleFocus = e => {
      setFocused(true);
      onFocus?.(e);
    };

    const handleBlur = e => {
      setFocused(false);
      onBlur?.(e);
    };

    const fieldClasses = cx(
      css.field,
      css.select,
      label && css.fieldWithLabel,
      icon && css.fieldWithIcon,
      error && css.fieldError
    );

    const labelClasses = cx(css.label, icon && !isFloating && css.labelWithIcon, isFloating && css.labelFloating);

    return (
      <div className={cx(css.wrapper, className)}>
        {icon && <span className={css.iconLeft}>{icon}</span>}

        <select
          ref={ref}
          id={uid}
          className={fieldClasses}
          disabled={disabled}
          value={currentValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          aria-invalid={!!error}
          aria-describedby={error ? `${uid}-error` : undefined}
          {...rest}
        >
          {placeholder && (
            <option
              value=""
              disabled
              hidden
            >
              {placeholder}
            </option>
          )}
          {options.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
            >
              {opt.label}
            </option>
          ))}
          {children}
        </select>

        {label && (
          <label
            htmlFor={uid}
            className={labelClasses}
          >
            {label}
          </label>
        )}

        {error && (
          <span
            id={`${uid}-error`}
            className={css.errorMessage}
            role="alert"
          >
            {error}
          </span>
        )}
      </div>
    );
  }
);

GlassSelect.displayName = "GlassSelect";

export {GlassInput, GlassTextarea, GlassSelect};
